import { create, tsx } from '@dojo/framework/core/vdom';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import * as css from './CaseStudy.m.css';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { Image } from '../../widgets/Image';
import { ListColumns } from '../../widgets/ListColumns';
import { Section } from '../../widgets/Section';
import { Summary } from '../../widgets/Summary';
import { Technologies } from '../../widgets/Technologies';
import { Text } from '../../widgets/Text';
import ContentWrapper from '../../widgets/ContentWrapper';
import SpeechBubble from '../../widgets/SpeechBubble';

const centricCardLeft = require('../../assets/images/work-mockups/centric-card-bottom-right.png');
const centricCardLeftHighRes = require('../../assets/images/work-mockups/centric-card-bottom-right@2x.png');
const centricCardRight = require('../../assets/images/work-mockups/centric-card-bottom-left.png');
const centricCardRightHighRes = require('../../assets/images/work-mockups/centric-card-bottom-left@2x.png');
const centricMockup = require('../../assets/images/work-mockups/centric-mockup.png');
const centricMockupHighRes = require('../../assets/images/work-mockups/centric-mockup@2x.png');

const factory = create();

export const CaseStudyCentric = factory(function CaseStudyCentric() {
	return (
		<div classes={css.root}>
			<Head
				title="Leadership through Expertise in modernizing Legacy Apps for Centric Software | SitePen"
				description="Learn how SitePen helped Centric convert their legacy application to a modern framework"
			/>

			<Hero background="level2RedOrange" align="center" blurbWidth="md" paddingBottom="sm">
				{{
					title: 'Case Study',
					xlTitle: 'Centric Software',
					blurb: (
						<virtual>
							Since the inception of enterprise JavaScript, companies have
							continuously built out custom software to meet the needs of their
							business. As the technological landscape has evolved, maintaining
							massive legacy applications has become increasingly difficult. How
							should a company approach a digital transformation while simultaneously
							maintaining the apps that its workforce and customers rely on?
							<Technologies
								icons={[
									'javascript',
									'typescript',
									'react',
									'intern',
									'dojo-toolkit',
									'mobx'
								]}
							/>
						</virtual>
					)
				}}
			</Hero>

			<Section type="leading" background>
				<ContentWrapper size="small">
					<Text type="section">The Situation</Text>
					<Text type="body">
						Centric Software provides powerful Product Lifecycle Management (PLM)
						software, offering a single source of truth for their customer’s
						product-related data.
					</Text>
					<Text type="body">
						When Centric 8 PLM was originally designed, its web application was using a
						popular, modern web framework at the time, the Dojo Toolkit. The application
						is a single-page application consisting of thousands of JavaScript modules
						that interface with a back-end server. Dojo Toolkit has powered the Centric
						8 PLM web client since its beginning, but the web ecosystem has evolved, and
						much has changed over the past decade.
					</Text>
					<Text type="body">
						Centric Software recognized some of the pain points of using an older
						framework and wanted to begin modernizing their codebase. Centric researched
						development partners they could trust with this important project to
						transform one of their most critical applications. Centric selected SitePen
						due to SitePen’s proven strengths in both first-generation and modern
						JavaScript frameworks. SitePen has Dojo Toolkit founders and maintainers on
						their team who also have extensive experience with modern frameworks such as
						React, Angular, and modern Dojo in large enterprise applications. Centric
						determined that SitePen was the ideal partner to help modernize their Dojo
						Toolkit application.
					</Text>
					<Text type="body">
						Ultimately, Centric approached SitePen to help determine the most effective
						way to address their needs:
					</Text>
					<ListColumns
						columnCount={1}
						style="disc"
						paddingLeft="sm"
						itemPaddingBottom="sm"
					>
						<Text type="body">
							<Text bold>Improve performance</Text> -{' '}
							<a href="https://www.sitepen.com/blog/what-is-a-2nd-generation-framework">
								Modern reactive frameworks
							</a>{' '}
							and newer CSS features offer substantial performance gains over
							decades-old first-generation frameworks.
						</Text>
						<Text type="body">
							<Text bold>Leverage reactive and responsive patterns</Text> - Using a
							combination of modern CSS and reactive components, building responsive,
							mobile-first components has never been easier.
						</Text>
						<Text type="body">
							<Text bold>Bring new features to market faster</Text> - Once the proper
							application architecture is in place, developing new features using
							reactive components and an application state store is a very efficient
							pattern. With the additional benefit of using TypeScript, many bugs are{' '}
							<a href="https://www.sitepen.com/blog/progressively-adopting-typescript-in-an-application">
								caught early
							</a>{' '}
							at build time, and not by the QA team, or worse, the customer.
						</Text>
						<Text type="body">
							<Text bold>Improve developer experience</Text> - New developers are
							entering the workforce with reactive framework education, so finding
							developers familiar with the development stack is important. Creating
							new, tested, reactive components is often much less work than it was
							using Dojo Toolkit. Additionally, developers find that the additional
							IDE-help offered by using TypeScript is invaluable.
						</Text>
						<Text type="body">
							<Text bold>Incremental migration</Text> - Due to the size and maturity
							of Centric 8 PLM, Centric could not feasibly rewrite the entire
							application in any modern framework in a single, standalone effort
							separate from their ongoing business needs.
						</Text>
					</ListColumns>
				</ContentWrapper>
			</Section>

			<Section shaded type="leading" background>
				<ContentWrapper size="small">
					<div classes={css.topImageContainer}>
						<img
							loading="lazy"
							classes={[css.topLeftImage]}
							src={centricCardLeft}
							srcset={`${centricCardLeftHighRes} 2x`}
							alt=""
						/>
						<img
							loading="lazy"
							classes={[css.topRightImage]}
							src={centricCardRight}
							srcset={`${centricCardRightHighRes} 2x`}
							alt=""
						/>
					</div>
					<div classes={css.mainImageContainer}>
						<Image
							src={centricMockup}
							highResSrc={centricMockupHighRes}
							align="center"
							lazy
						/>
					</div>
					<Text type="section">The Solution</Text>
					<Text type="body">
						SitePen consulted with Centric Software to build a plan unique to their
						environment and then start incrementally transitioning their legacy Dojo
						Toolkit application into TypeScript and a modern framework without
						interrupting ongoing feature development.
					</Text>
					<Text type="body">
						While SitePen is an active contributor to Dojo, SitePen chooses frameworks
						based on the specific needs of their customers. In this situation, React and
						its ecosystem was determined by SitePen and Centric to be the best solution
						today.
					</Text>
					<Text type="body">
						SitePen began this re-architecture effort by integrating new technologies
						into the build optimization system. Centric 8 PLM uses a highly customized
						Webpack configuration that bundles their Dojo Toolkit modules into a single
						JavaScript file. SitePen tailored a custom TypeScript configuration
						specifically for Centric 8 PLM, designed to maximize interoperability with
						Dojo Toolkit and facilitating new development efforts. With this new build
						system in place, Centric can write new modules in TypeScript that seamlessly
						interact with existing Dojo Toolkit modules. Centric can also start
						converting existing JavaScript modules to TypeScript without impacting the
						rest of the application. SitePen was also able to introduce several
						optimizations to the Webpack configuration and outline a path forward for
						Centric to take advantage of more advanced Webpack features like bundle
						splitting and async modules.
					</Text>
					<Text type="body">
						Integrating a new framework into an existing application requires careful
						planning and forethought by architects highly experienced in both the old
						and new frameworks. Without this guidance, it is easy to fall into a
						situation where the two frameworks are inefficiently converting data and
						application logic back and forth. Every time one of these conversions take
						place, there is typically a performance cost. SitePen dove into Centric 8
						PLM to understand and identify the optimal insertion point for React that
						would result in the smallest performance impact while still offering maximum
						productivity gains. This enabled Centric to begin feature development on
						several new features created with React and TypeScript.
					</Text>
					<Text type="body">
						Having a central place to store and share the application state is key to
						maintaining a large-scale reactive application. Before such techniques were
						commonplace, Dojo Toolkit relied heavily on the publish-subscribe pattern,
						in which parts of the application would subscribe for notifications from
						other parts of the application. While it solves the problem of communication
						between components, this pattern tends to be challenging to debug, as the
						code cannot simply be traced to its source, and can often lead to difficult
						to diagnose bugs. SitePen introduced an application store, MobX, into
						Centric 8 PLM and demonstrated how to integrate this store into the new
						React-based features as well as transition old publish-subscribe Dojo
						Toolkit components to use MobX as well. This provides a single place in
						which Centric can reliably store observable application state and have it
						available in both legacy and new features.
					</Text>
					<Text type="body">
						For some commonly used web UI components, creating them from scratch is
						often not worth the effort. For example, writing a data grid or styling
						components that fit Material UI specifications could easily require as much
						effort as developing the feature itself. The React ecosystem is vast, and
						with thousands of third-party options to choose from, finding the best fit
						for a specific use case can be difficult. SitePen helped evaluate and
						recommend optimal right third-party libraries for each situation, providing
						proof-of-concept examples and documentation weighing the pros and cons for
						each. This enabled Centric’s developers to concentrate on the features that
						differentiate their product, rather than wading through and evaluating
						potential libraries or reinventing the wheel.
					</Text>
				</ContentWrapper>
			</Section>

			<Section>
				<ContentWrapper>
					<Summary collapse="large" blurbGutter="medium" imageGutter="none">
						{{
							title: 'The Result',
							blurb: (
								<p>
									With SitePen's help,{' '}
									<a
										href="https://www.centricsoftware.com/fashion-plm/"
										target="_blank"
										rel="noopener"
									>
										Centric Software
									</a>{' '}
									is shipping their first modern React features without having to
									do a complete rewrite of their application, enabling Centric to
									get modern features to market more efficiently and with
									confidence from SitePen’s extensive web application knowledge
									and experience. Additionally Centric has a solid plan with
									well-documented patterns for the continued incremental
									modernization from Dojo Toolkit into TypeScript and React!
								</p>
							),
							image: (
								<SpeechBubble>
									{{
										main:
											'We have an award winning and innovative product, and wanted a UI architecture to match. SitePen’s team of experts helped pave the path toward a future of even faster innovation with very timely advice and engineering.',
										subtitle1: 'VP, Product Management',
										subtitle2: 'Centric Software'
									}}
								</SpeechBubble>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section type="trailing" shaded>
				<ConnectStrip trailing />
			</Section>
		</div>
	);
});

export default CaseStudyCentric;
